/**
 * Bulk-download constraints shared by the client (UX: live selection total + a
 * disabled action) and the server (boundary enforcement). Kept beside the upload
 * constraints so both size limits live in one place.
 *
 * `downloadDocumentsAsZip` buffers the whole archive in memory (base64 inside a
 * Magellan return value — not a stream), peaking at ~4.7× the selection. With one
 * replica an OOM is an outage, so the selection must be bounded on **both** axes:
 * a count (the UI only ever showed a page's worth, but the server enforced nothing)
 * and a total byte size (the real memory bound). See
 * docs/plans/2026-07-29-bulk-download-bounds.md.
 */

/**
 * Maximum number of Documents in one bulk download. A real constant, not the
 * `DOCUMENTS_PAGE_SIZE` pagination side effect it used to be emergent from —
 * consumed by both the UI and `ManageDocumentsInputSchema` so changing the page
 * size cannot move it.
 */
export const MAX_BULK_DOWNLOAD_FILE_COUNT = 20;

/**
 * Maximum total raw size of a bulk selection. The binding memory limit: at ~4.7×
 * amplification a 200 MB selection peaks near 1.1 GB, which co-exists with a maximum
 * concurrent upload inside the 2 GiB pod. Provisional until measured against integ.
 * Only bites when 20 Documents average more than 10 MB — unreachable by typical Belege.
 */
export const MAX_BULK_DOWNLOAD_SIZE_BYTES = 200 * 1024 * 1024;

/** Human-readable bulk-download size limit for UI hints and validation messages (German UI). */
export const MAX_BULK_DOWNLOAD_SIZE_LABEL = "200 MB";

export type BulkDownloadRejectionReason = "count" | "size";

/**
 * Returns a rejection if the selection violates the count or total-size limit, else
 * `undefined`. Sizes are summed from Document **metadata** — never by reading blobs,
 * which would exhaust memory while deciding whether it would exhaust memory.
 */
export const checkBulkDownload = (
    files: readonly { sizeBytes: number }[]
): BulkDownloadRejectionReason | undefined => {
    if (files.length > MAX_BULK_DOWNLOAD_FILE_COUNT) {
        return "count";
    }
    const totalBytes = files.reduce((sum, file) => sum + file.sizeBytes, 0);
    if (totalBytes > MAX_BULK_DOWNLOAD_SIZE_BYTES) {
        return "size";
    }
    return undefined;
};
